(() => {
  if (window.IzyPopupQueue) return;
  const script = document.currentScript;
  const cookieSelector = '#shopify-pc__banner, #shopify-pc__prefs__dialog, privacy-banner[open]';
  const dialogSelector = 'dialog[open], [role="dialog"][open], .pswp--open';
  const themeDialogs = new Set(['cart-drawer', 'quick-buy-modal', 'header-search', 'header-sidebar', 'x-modal', 'newsletter-popup', 'privacy-banner']);
  let newsletterEligible = false;
  try {
    const key = 'izy:popup-navigation:v1';
    let path = script.dataset.pagePath;
    const root = script.dataset.localeRoot.replace(/\/$/, '');
    if (root && path.startsWith(root + '/')) path = path.slice(root.length);
    else if (root && path === root) path = '/';
    path = path.replace(/\/$/, '') || '/';
    const saved = JSON.parse(sessionStorage.getItem(key));
    newsletterEligible = saved?.qualified === true || Boolean(saved?.firstPath && saved.firstPath !== path);
    sessionStorage.setItem(key, JSON.stringify({ firstPath: saved?.firstPath || path, qualified: newsletterEligible }));
  } catch (_) {}

  class PopupQueue {
    constructor() {
      this.entries = new Map();
      this.active = null;
      this.privacy = 'loading';
      this.notBefore = 0;
      this.newsletterEligible = newsletterEligible;
      document.addEventListener('visitorConsentCollected', () => this.schedule());
      document.addEventListener('keydown', event => {
        // Shopify's hidden banner must not take the first Tab focus from the language dialog.
        if (event.key === 'Tab' && document.documentElement.hasAttribute('data-izy-localization-pending')) event.stopPropagation();
      });
      document.addEventListener('dialog:after-hide', event => this.closed(event.target), true);
      document.addEventListener('close', event => this.closed(event.target), true);
      window.addEventListener('pageshow', () => this.schedule());
      const start = () => {
        this.observer = new MutationObserver(() => this.schedule());
        this.observer.observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['open', 'hidden', 'style', 'class', 'aria-hidden', 'data-izy-localization-pending'] });
        this.loadPrivacy();
        if (!document.querySelector('izy-localization-popup')) this.finishLocalization();
      };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
      else start();
    }

    visible(element) {
      return element.isConnected && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden' && getComputedStyle(element).display !== 'none';
    }

    beginLocalization(element) {
      this.localizationElement = element;
      clearTimeout(window.izyLocalizationGateTimeout);
      document.documentElement.setAttribute('data-izy-localization-pending', '');
      this.schedule();
    }

    finishLocalization(element) {
      if (element && this.localizationElement && this.localizationElement !== element) return;
      clearTimeout(window.izyLocalizationGateTimeout);
      document.documentElement.removeAttribute('data-izy-localization-pending');
      this.localizationElement = null;
      this.schedule();
    }

    cookiePending() {
      if (Array.from(document.querySelectorAll(cookieSelector)).some(element => this.visible(element))) return true;
      if (this.privacy === 'loading') return true;
      try {
        const api = window.Shopify?.customerPrivacy;
        const consent = api?.currentVisitorConsent();
        const decided = consent && ['analytics', 'marketing', 'preferences'].some(key => consent[key] === 'yes' || consent[key] === 'no');
        return Boolean(api?.shouldShowBanner() && !decided);
      } catch (_) { return true; }
    }

    loadPrivacy() {
      const complete = error => {
        clearTimeout(this.privacyTimeout);
        this.privacy = error || !window.Shopify?.customerPrivacy ? 'unavailable' : 'ready';
        this.schedule();
      };
      this.privacyTimeout = setTimeout(() => complete(true), 8000);
      if (window.Shopify?.customerPrivacy) complete(false);
      else if (window.Shopify?.loadFeatures) {
        try { window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], complete); }
        catch (_) { complete(true); }
      } else complete(true);
    }

    request(element, open, close, priority = 10, automatic = false) {
      if (window.Shopify?.designMode) return open();
      if (this.entries.has(element)) return this.entries.get(element).promise;
      let resolve;
      const promise = new Promise(done => { resolve = done; });
      this.entries.set(element, { element, open, close, priority, automatic, resolve, promise, shown: false, suspending: false });
      this.schedule();
      return promise;
    }

    cancel(element) {
      const entry = this.entries.get(element);
      if (!entry) return;
      this.entries.delete(element);
      if (this.active === entry) this.active = null;
      entry.resolve();
      this.notBefore = Date.now() + 400;
      this.schedule();
    }

    closed(element) {
      const entry = this.entries.get(element) || this.entries.get(element.closest?.('izy-localization-popup'));
      if (entry && !entry.suspending) this.cancel(entry.element);
      this.schedule();
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.drain(); });
    }

    externalDialog(active) {
      return Array.from(document.querySelectorAll(dialogSelector)).some(element =>
        this.visible(element) && element !== active?.element && !active?.element.contains(element)
      );
    }

    async suspend(entry) {
      if (entry.suspending) return;
      entry.suspending = true;
      const visibility = entry.element.style.visibility;
      entry.element.style.visibility = 'hidden';
      try { await entry.close(); }
      finally {
        entry.element.style.visibility = visibility;
        entry.suspending = false;
        entry.shown = false;
        if (this.active === entry) this.active = null;
        this.notBefore = Date.now() + 400;
        this.schedule();
      }
    }

    drain() {
      for (const entry of this.entries.values()) if (!entry.element.isConnected) this.cancel(entry.element);
      const pending = Array.from(this.entries.values()).filter(entry => !entry.shown && !entry.suspending).sort((a, b) => b.priority - a.priority);
      const localization = document.documentElement.hasAttribute('data-izy-localization-pending');
      const cookie = !localization && this.cookiePending();
      if (this.active) {
        if (cookie || this.active.automatic && (this.externalDialog(this.active) || pending[0]?.priority > this.active.priority)) this.suspend(this.active);
        return;
      }
      if (!pending.length || cookie || this.externalDialog(null)) return;
      const entry = pending.find(candidate => {
        if (localization && candidate.automatic && candidate.element.localName !== 'izy-localization-popup') return false;
        return !candidate.automatic || candidate.element.localName === 'izy-localization-popup' || this.privacy === 'ready';
      });
      if (!entry) return;
      const wait = this.notBefore - Date.now();
      if (wait > 0) {
        clearTimeout(this.wake);
        this.wake = setTimeout(() => this.schedule(), wait);
        return;
      }
      entry.shown = true;
      this.active = entry;
      Promise.resolve().then(entry.open).then(entry.resolve).catch(() => this.cancel(entry.element));
    }
  }

  const queue = window.IzyPopupQueue = new PopupQueue();
  customElements.whenDefined('newsletter-popup').then(() => {
    let prototype = customElements.get('newsletter-popup').prototype;
    while (prototype && !Object.hasOwn(prototype, 'show')) prototype = Object.getPrototypeOf(prototype);
    if (!prototype) return;
    const original = prototype.show;
    prototype.show = function (...args) {
      if (!themeDialogs.has(this.localName) || this.open || window.Shopify?.designMode || this.localName === 'privacy-banner') return original.apply(this, args);
      const automatic = this.localName === 'newsletter-popup';
      return queue.request(this, () => original.apply(this, args), () => this.hide(), automatic ? 10 : 50, automatic);
    };
  });
})();
